import {
  ExpandLess,
  ExpandMore,
  CheckCircle,
  Info,
  Refresh,
  Download,
  ReportProblem,
} from '@mui/icons-material'
import { List, Collapse, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { selectFeatureIncludes, FeatureDict } from '@authed/selectors'
import {
  PostWithTargetConnector,
  IDatasetIntegrationExecution,
} from '@datasets/dataset-details/datasetHistoryV2/types'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { PostStatus } from '@global/types'
import { useAppSelector, useAppDispatch } from '@redux/hooks'
import { api } from 'api'
import { Target } from './components/target'
import * as css from './styles'
import { t } from '@lingui/core/macro'

interface Props {
  integrationName: string | undefined
  onClick: (id) => void
  open: Record<string, boolean>
  datasetPosts: PostWithTargetConnector[]
  submissionId: string | number
  assignmentsExecution: Record<number, IDatasetIntegrationExecution[]>
}

interface IMutationReRunDataset {
  submissionId: string | number
  postId: string | number
}

interface IMutationError {
  [key: string]: any
  status: string
  data: { message: string }
}

// Check submission status against enum, convert enum to string for evaluation.
function isPostStatusEqual(status: PostStatus, statusType: PostStatus) {
  return status?.toString().toLowerCase() === statusType?.toString()
}

interface TargetsRouterParams {
  id: string
}

export const Targets = ({
  integrationName,
  open,
  onClick,
  datasetPosts,
  submissionId,
  assignmentsExecution,
}: Props) => {
  const { id: datasetId } = useParams<TargetsRouterParams>()
  const dispatch = useAppDispatch()
  const [API_DEBUG_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASET_DETAILS_API_DEBUG]),
  )

  const queryClient = useQueryClient()

  const mutationDatasetPost = useMutation({
    mutationFn: ({ submissionId: subID, postId }: IMutationReRunDataset) =>
      api.post(`/api/v1/account/dataset/submission/${subID}/${postId}/run`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['datasetHistorySubmissions', +datasetId],
      })
    },
    onError: (error: IMutationError) => {
      const { status, data } = error
      const { message } = data || {}
      dispatch(
        showSnackbar({
          message: t`${status}: Unable to re-run dataset. \n ${message}`,
          type: 'ERROR',
        }),
      )
    },
  })

  if (!datasetPosts.length) return null

  return (
    <List sx={{ marginTop: '10px' }} className="outbound-table-drawer-targets">
      {datasetPosts?.map(post => {
        const { remote_target_id: multi_remote_target_Id, remote_realm_id: multi_remote_realm_id } =
          post?.multi_target || {}
        const { name, realm_name, remote_target_id } = post?.target || {}
        const isTargetAdobeAnalytics = integrationName === 'Adobe Analytics'
        const adobeTargetNamePart1 = multi_remote_realm_id || realm_name
        const adobeTargetNamePart2 = multi_remote_target_Id || remote_target_id
        const adobeTargetName = `${adobeTargetNamePart1} >> ${adobeTargetNamePart2}`

        // Adobe Analytics is a special case where the target name is a combination of realm_name and remote_target_id
        const remoteJobId = post.remote_job_id || ''
        const itemText = isTargetAdobeAnalytics
          ? adobeTargetName
          : name || t`Remote job ID: ${remoteJobId}`

        return (
          <div key={post.id}>
            <ListItemButton
              onClick={() => {
                onClick({ [post.id]: !open[post.id] })
              }}
              disableRipple={true}
            >
              <ListItemIcon>
                {!post.status ? <ReportProblem color="error" /> : null}
                {isPostStatusEqual(post.status, PostStatus.success) ? (
                  <CheckCircle color="success" />
                ) : null}
                {isPostStatusEqual(post.status, PostStatus.complete) ? (
                  <CheckCircle color="success" />
                ) : null}
                {isPostStatusEqual(post.status, PostStatus.pending) ? (
                  <Info color="primary" />
                ) : null}
                {isPostStatusEqual(post.status, PostStatus.error) ? (
                  <ReportProblem color="error" />
                ) : null}
              </ListItemIcon>
              <ListItemText
                primary={itemText}
                primaryTypographyProps={{ sx: { fontSize: '12px', overflowWrap: 'break-word' } }}
              />
              {API_DEBUG_ENABLED && (
                <css.ActionIcons>
                  <Refresh
                    color="primary"
                    fontSize="small"
                    onClick={e => {
                      e.stopPropagation()
                      mutationDatasetPost.mutate({ submissionId, postId: post.id })
                    }}
                  />
                  <Download
                    color="primary"
                    fontSize="small"
                    onClick={e => {
                      e.stopPropagation()
                      void downloadPostZip({
                        datasetId: +datasetId,
                        datasetSubmissionId: post.dataset_submission_id,
                        postId: post.id,
                      })
                    }}
                  />
                </css.ActionIcons>
              )}
              {open[post.id] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={open[post.id]}>
              <Target post={post} assignmentsExecution={assignmentsExecution} />
            </Collapse>
          </div>
        )
      })}
    </List>
  )
}

interface IDownloadPost {
  datasetId: number
  datasetSubmissionId: number
  postId: number
}

async function downloadPostZip({ datasetId, datasetSubmissionId, postId }: IDownloadPost) {
  const { data } = await api.get<{ token: string }>(
    `/api/v1/account/dataset/${datasetId}/csv/token`,
  )
  const location = `/api/v1/account/dataset/${datasetId}/submissions/${datasetSubmissionId}/apizip/${data.token}?postId=${postId}`
  window.location.href = location
}
